import { ArrowLeft } from "lucide-react"
import { useRef, useState, useEffect } from "react"
import { supabase } from "../lib/supabase"
import { useTienePermiso } from "../App"
import { AnalisisQuimicoForm } from "./AnalisisQuimicoForm"
import { MET7211Form } from "./MET-7.2-11-Form"
import { MET7212Form } from "./MET-7.2-12-Form"
import { MET7219Form } from "./MET-7.2-19-Form"
import { NMXa073Form } from "./NMX-A-073-Form"
import { NMXA083Form } from "./NMX-A-083-Form"
import { NMXA105C06Form } from "./NMX-A-105-C06-Form"
import { NMXA105E04Form } from "./NMX-A-105-E04-Form"
import { NMXA109Form } from "./NMX-A-109-Form"
import { NMXA129453Form } from "./NMX-A-12945-3-Form"
import { NMXA134Form } from "./NMX-A-134-Form"
import { NMXA172Form } from "./NMX-A-172-Form"
import { NMXA221Form } from "./NMX-A-221-Form"
import { NMXA225Form } from "./NMX-A-225-Form"
import { NMXA228Form } from "./NMX-A-228-Form"
import { NMXA229Form } from "./NMX-A-229-Form"
import { NMXA230Form } from "./NMX-A-230-Form"
import { NMXA2755Form } from "./NMX-A-275-5-Form"
import { NMXA3801Form } from "./NMX-A-3801-Form"
import { NMXA4920Form } from "./NMX-A-4920-Form"
import { NMXA50776330Form } from "./NMX-A-5077-6330-Form"
import { NMXA72112Form } from "./NMX-A-7211-2-Form"
import { NMXA90731Form } from "./NMX-A-9073-1-Form"
import { NMXA105D01Form } from "./NMX-A-105-D01-Form"
import { NMXA105B02Form } from "./NMX-A-105-B02-Form"
import { NMXA059_1Form } from "./NMX-A-059-1-Form"
import { NMXA059_2Form } from "./NMX-A-059-2-Form"
import { AATCCForm } from "./AATCC-TM127-Form"
import { NMXS051Form } from "./NMX-S-051-Form"
import { PruebaAdicionalForm } from "./PruebaAdicionalForm"
import { Button } from "./ui/button"
import { Card, CardContent } from "./ui/card"
import { usePedidos } from "../hooks/usePedidos"
import { guardarPrueba, revisarPrueba, supabaseToFormData, registrarEdicion, actualizarAceptado, actualizarIncluirReporteCliente } from "../services/pruebasService"
import { toast } from "sonner"
import { PedidoDetail } from "./pedidos/PedidoDetail"
import { PedidosList } from "./pedidos/PedidosList"
import { ReaperturasPendientesPanel } from "./pedidos/ReaperturasPendientesPanel"

// ─── TYPES ───────────────────────────────────────────────────────────────────

// Re-export Pedido from types so existing imports from PedidosView still work
export type { Pedido } from "../types/pedido"
import type { Pedido } from "../types/pedido"

interface PedidosViewProps {
  pedidos: Pedido[];
  userName: string;
  pruebasGuardadas?: { [pedidoId: string]: { [prueba: string]: { row_id: string } } };
  userRol: string;
  onRefreshBackend?: () => void;
  puedeAutorizarReapertura?: boolean;
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

export function PedidosView({
  pedidos,
  userName,
  pruebasGuardadas: pruebasGuardadasProp = {},
  userRol,
  onRefreshBackend,
  puedeAutorizarReapertura = false,
}: PedidosViewProps) {
  const [selectedPedido, setSelectedPedido] = useState<Pedido | null>(null);
  const [currentView, setCurrentView] = useState<'list' | 'detail' | 'form'>('list');
  const [currentPrueba, setCurrentPrueba] = useState<string>('');
  const [formInitialData, setFormInitialData] = useState<any>(null);
  const [formMode, setFormMode] = useState<'create' | 'view' | 'admin-edit' | 'continue-draft'>('create');
  const [filtroTarjeta, setFiltroTarjeta] = useState('');
  const [selectedPruebaKey, setSelectedPruebaKey] = useState<string | null>(null);
  const [currentPruebaIndex, setCurrentPruebaIndex] = useState<number>(-1);
  const [showDocumentoUploadModal, setShowDocumentoUploadModal] = useState(false);
  const [documentoUploadInfo, setDocumentoUploadInfo] = useState<{ rowId: string; prueba: string; nomId?: string; pedidoId: string; origenNumero: string } | null>(null);
  const isSavingRef = useRef(false);
  // Snapshot estable de initialData por sesión de formulario (LLENAR/CONTINUAR/REVISAR/EDITAR).
  // Se invalida solo al entrar a una prueba (ver handleLlenarPrueba/handleContinuarBorrador/
  // handleRevisarPrueba/handleEditarPrueba) — nunca se recalcula por re-renders de fondo
  // (ej. el ROW_ID/EN_BORRADOR optimista que pone el autosave), o cada autosave exitoso
  // pisaría con un snapshot viejo lo que el usuario ya escribió en el formulario.
  const initialDataCacheRef = useRef<{ value: any } | null>(null);

  const { pedidosState, setPedidosState, pruebasGuardadas } = usePedidos(pedidos, pruebasGuardadasProp);

  const puedeCapturar = useTienePermiso('capturar_resultado');
  const puedeCerrarPedido = useTienePermiso('cerrar_pedido');
  const puedeVerBitacoras = useTienePermiso('ver_bitacoras');
  const puedeReabrir = useTienePermiso('reabrir');

  const ADMIN_EMAILS = ['emaviri12@gmail.com', 'roberto.ochoa@oacsolutions.io'];
  const userEmail = typeof window !== 'undefined' ? localStorage.getItem('userEmail') || '' : '';
  const esAdmin = ADMIN_EMAILS.includes(userEmail);

  const [modoEdicionAdmin, setModoEdicionAdmin] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminModalEmail, setAdminModalEmail] = useState('');
  const [adminModalHoras, setAdminModalHoras] = useState('24');
  const [adminModalGuardando, setAdminModalGuardando] = useState(false);

  // Detectar ?admin_edit=true en la URL y abrir modal si es admin
  useEffect(() => {
    if (!esAdmin) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('admin_edit') === 'true') {
      setShowAdminModal(true);
      // Limpiar el param de la URL sin recargar
      const url = new URL(window.location.href);
      url.searchParams.delete('admin_edit');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  // Verificar si el usuario actual tiene acceso de edición en el pedido seleccionado
  useEffect(() => {
    if (!selectedPedido) return;
    const verificarAcceso = async () => {
      const { data } = await supabase
        .from('pedido_acceso_edicion')
        .select('id')
        .eq('pedido_id', selectedPedido.id)
        .eq('email_autorizado', userEmail)
        .gt('expira_en', new Date().toISOString())
        .limit(1);
      setModoEdicionAdmin(!!(data && data.length > 0));
    };
    verificarAcceso();
  }, [selectedPedido?.id]);

  const handleHabilitarAcceso = async () => {
    if (!selectedPedido || !adminModalEmail || !adminModalHoras) return;
    setAdminModalGuardando(true);
    try {
      const expira = new Date();
      expira.setHours(expira.getHours() + parseInt(adminModalHoras));
      const { error } = await supabase
        .from('pedido_acceso_edicion')
        .insert({
          pedido_id: selectedPedido.id,
          email_autorizado: adminModalEmail.trim().toLowerCase(),
          habilitado_por: userEmail,
          expira_en: expira.toISOString(),
        });
      if (error) throw new Error(error.message);
      setShowAdminModal(false);
      setAdminModalEmail('');
      setAdminModalHoras('24');
    } catch (err: any) {
      alert('Error: ' + err.message);
    } finally {
      setAdminModalGuardando(false);
    }
  };

  const latestSelectedPedido = pedidosState.find(p => p.id === selectedPedido?.id) || selectedPedido;
  const isReview = formMode === 'view';
  const initialData = (formMode === 'view' || formMode === 'admin-edit' || formMode === 'continue-draft') ? formInitialData : undefined;
  const mode = isReview ? 'view' : 'create';

  // ─── HANDLERS ──────────────────────────────────────────────────────────────

  const handleAddPrueba = (pedidoId: string, pruebaNombre: string) => {
    setPedidosState(prev => prev.map(p =>
      p.id === pedidoId
        ? { ...p, pruebasSolicitadas: [...p.pruebasSolicitadas, { nombre: pruebaNombre, ROW_ID: '', nomId: '', ACEPTADO: '' }] }
        : p
    ));
    setSelectedPedido(prev =>
      prev && prev.id === pedidoId
        ? { ...prev, pruebasSolicitadas: [...prev.pruebasSolicitadas, { nombre: pruebaNombre, ROW_ID: '', nomId: '', ACEPTADO: '' }] }
        : prev
    );
  };

  const handleLlenarPrueba = (pedido: Pedido, prueba: string, pruebaIndex: number) => {
    initialDataCacheRef.current = null;
    setFormInitialData(null);
    setSelectedPruebaKey(null);
    setCurrentPrueba(prueba);
    setCurrentPruebaIndex(pruebaIndex);
    setCurrentView('form');
    setFormMode('create');
  };

  const handleBackToDetail = () => {
    setCurrentView('detail');
    setCurrentPrueba('');
  };

  // Autosave de NMX-A-083 (piloto): refleja el rowId/nomId creado/actualizado por el
  // debounce del formulario en el estado local, sin esperar un refresh completo del backend.
  const handleAutosavePrueba = (result: { rowId: string; nomId: string }) => {
    const pedidoId = selectedPedido?.id;
    if (!pedidoId) return;
    // selectedPruebaKey (`${nombre}__${rowId}`) identifica de forma estable con qué prueba
    // arrancó esta sesión de formulario (se limpia a null solo para altas nuevas, ver
    // handleLlenarPrueba). currentPruebaIndex es posicional y puede quedar obsoleto si
    // pruebasSolicitadas cambia mientras el formulario sigue abierto — usarlo directo aquí
    // puede pisar el ROW_ID/nomId de OTRA prueba distinta. Por eso el match por contenido
    // tiene prioridad; el índice solo aplica para altas nuevas (sin ROW_ID todavía).
    const [selNombre, selRowId] = (selectedPruebaKey || '').split('__');
    const updatePrueba = (pr: any, idx: number) => {
      const matchesByContent = !!selRowId && pr.nombre === selNombre && String(pr.ROW_ID) === String(selRowId);
      const matchesByIndex = !selRowId && idx === currentPruebaIndex;
      return (matchesByContent || matchesByIndex)
        ? { ...pr, ROW_ID: result.rowId, nomId: result.nomId, EN_BORRADOR: true }
        : pr;
    };
    setPedidosState(prev => prev.map(p =>
      p.id === pedidoId ? { ...p, pruebasSolicitadas: p.pruebasSolicitadas.map(updatePrueba) } : p
    ));
    setSelectedPedido(prev =>
      prev && prev.id === pedidoId
        ? { ...prev, pruebasSolicitadas: prev.pruebasSolicitadas.map(updatePrueba) }
        : prev
    );
  };

  // Reabrir una prueba en borrador en modo 'create' (no 'view') para que el autosave
  // y el botón "Guardar" sigan habilitados sobre los datos ya precargados.
  const handleContinuarBorrador = async (pedido: Pedido, prueba: string, rowId: string) => {
    const key = `${prueba}__${rowId}`;
    setSelectedPruebaKey(key);
    const pruebaIdx = pedido.pruebasSolicitadas.findIndex(pr => pr.nombre === prueba && pr.ROW_ID === rowId);
    setCurrentPruebaIndex(pruebaIdx);
    try {
      const row = await revisarPrueba(prueba, rowId);
      const mappedData = supabaseToFormData(prueba, row);
      const pruebaObj = pedido.pruebasSolicitadas.find(pr => pr.nombre === prueba && pr.ROW_ID === rowId);
      initialDataCacheRef.current = null;
      // El analista que retoma el borrador pasa a ser quien realiza la prueba: sin esto
      // `realizo` conserva al analista que inició el borrador y el autosave lo vuelve a guardar.
      setFormInitialData({ ...mappedData, ...(userName ? { realizo: userName } : {}), ROW_ID: rowId, nomId: pruebaObj?.nomId || '', ACEPTADO: pruebaObj ? (pruebaObj.ACEPTADO || '') : (row.aceptado || '') });
      setFormMode('continue-draft');
      setSelectedPedido(pedido);
      setCurrentPrueba(prueba);
      setCurrentView('form');
    } catch (err: any) {
      alert("Error al consultar: " + (err.message || err));
    }
  };

  const handleBackToList = () => {
    setSelectedPedido(null);
    setCurrentView('list');
    setCurrentPrueba('');
  };

  const handleToggleReporteCliente = async (nomId: string, incluir: boolean) => {
    setPedidosState(prev => prev.map(p => ({
      ...p,
      pruebasSolicitadas: p.pruebasSolicitadas.map(pr =>
        pr.nomId === nomId ? { ...pr, INCLUIR_REPORTE_CLIENTE: incluir } : pr
      ),
    })));
    try {
      await actualizarIncluirReporteCliente(nomId, incluir);
    } catch (err: any) {
      alert("Error al actualizar: " + (err.message || err));
      setPedidosState(prev => prev.map(p => ({
        ...p,
        pruebasSolicitadas: p.pruebasSolicitadas.map(pr =>
          pr.nomId === nomId ? { ...pr, INCLUIR_REPORTE_CLIENTE: !incluir } : pr
        ),
      })));
    }
  };

  const marcarPruebaComoGuardada = (pedidoId: string, prueba: string, row_id: string, aceptado?: string) => {
    // This is handled by the parent via onRefreshBackend; local update for immediate feedback
    setPedidosState(prev => prev.map(p =>
      p.id === pedidoId
        ? {
          ...p,
          pruebasSolicitadas: p.pruebasSolicitadas.map(pr =>
            pr.nombre === prueba
              ? { ...pr, ROW_ID: row_id, ACEPTADO: aceptado || pr.ACEPTADO }
              : pr
          ),
        }
        : p
    ));
  };

  const handleSaveForm = async (formData: any) => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    try {
      const pedidoId = selectedPedido!.id;
      // Use latestSelectedPedido (reflects post-refresh state) to avoid stale nomId lookups
      const currentPedido = latestSelectedPedido || selectedPedido;
      // currentPruebaIndex es una referencia posicional: se captura al abrir el formulario y
      // puede quedar obsoleta si pruebasSolicitadas cambia de orden/contenido mientras el
      // formulario está abierto (ej. otro usuario reordena o edita el pedido en paralelo). Si
      // eso pasa, el índice apunta a OTRA prueba que también puede tener un nomId válido —pero
      // equivocado—, lo que hace que se acepte/edite la prueba incorrecta en silencio. Por eso
      // el match por contenido (nombre + ROW_ID, ambos estables) tiene prioridad sobre el índice;
      // el índice solo se usa como último recurso cuando todavía no hay ROW_ID (alta nueva).
      const pruebaByContent = formData?.ROW_ID
        ? (currentPedido?.pruebasSolicitadas ?? [])
            .find((pr: any) => pr.nombre === currentPrueba && String(pr.ROW_ID) === String(formData.ROW_ID))
        : undefined;
      const pruebaByIndex = currentPruebaIndex >= 0
        ? currentPedido?.pruebasSolicitadas[currentPruebaIndex]
        : undefined;
      const pruebaTarget = pruebaByContent || pruebaByIndex;
      const existingNomId = pruebaTarget?.nomId || (formData?._EDIT_MODE ? formData?.nomId : undefined) || undefined;
      const existingRowId = pruebaTarget?.ROW_ID || formData?.ROW_ID || '';
      const isEditMode = formData?._EDIT_MODE === true;

      // guardarPrueba handles both INSERT into prueba_XXXX and CREATE/UPDATE noms atomically
      // Si ya existe un ROW_ID (admin-edit, isEditMode, o un borrador autosaveado) hacemos
      // UPDATE en vez de INSERT, para no duplicar la fila.
      const rowIdParaGuardar = existingRowId || undefined;
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(currentPrueba, pedidoId, formData, existingNomId, rowIdParaGuardar, false);
      const responseRowId = String(pruebaId);
      // Only carry ACEPTADO from formData in edit mode — in create mode a new prueba starts as empty
      const responseAceptado = isEditMode ? 'aceptado' : (!existingNomId ? '' : (formData?.ACEPTADO || formData?.aceptado || ''));

      // Solo registrar en el historial de ediciones cuando viene del flujo EDITAR → Guardar
      // edición (isEditMode). Completar un borrador (autosave + CONTINUAR + Guardar) también
      // cumple existingRowId/formInitialData, pero no es una edición real.
      // Se usa savedNomId (garantizado válido tras guardarPrueba) en vez de existingNomId
      // (pre-save): si la prueba no tenía todavía un registro en noms cargado localmente,
      // existingNomId venía vacío y este bloque se saltaba en silencio, dejando la edición
      // sin registrar aunque el guardado sí ocurriera.
      if (isEditMode && existingRowId && savedNomId && formInitialData) {
        try {
          const pruebaYaTeniaRastro = (latestSelectedPedido?.pruebasSolicitadas ?? [])
            .find((pr: any) => pr.ROW_ID === String(existingRowId) && pr.nombre === currentPrueba)
            ?.EDITADO === true;
          const silencioso = modoEdicionAdmin && !pruebaYaTeniaRastro;
          await registrarEdicion(
            savedNomId,
            currentPrueba,
            existingRowId,
            formInitialData,
            formData,
            userName,
            silencioso,
          );
        } catch (edicionErr) {
          console.warn('No se pudo registrar la edición:', edicionErr);
        }
      }

      // If editing an existing prueba with _EDIT_MODE flag, mark as aceptado.
      // Gate on savedNomId (ver comentario arriba) en vez de existingNomId para que el
      // estatus realmente se persista en vez de solo reflejarse de forma optimista en la UI.
      if (isEditMode && savedNomId) {
        try {
          await actualizarAceptado(responseRowId, 'aceptado', undefined, undefined, savedNomId);
        } catch (aceptadoErr) {
          console.warn('No se pudo marcar como aceptado:', aceptadoErr);
        }
      }

      // Update local state so the UI reflects the saved ROW_ID and nomId immediately
      const pruebaYaTeniaRastroLocal = (latestSelectedPedido?.pruebasSolicitadas ?? [])
        .find((pr: any) => pr.ROW_ID === String(existingRowId) && pr.nombre === currentPrueba)
        ?.EDITADO === true;
      const editadoFields = isEditMode && (!modoEdicionAdmin || pruebaYaTeniaRastroLocal)
        ? { EDITADO: true, EDITADO_POR: userName }
        : {};
      // Mismo criterio que arriba: preferir match por contenido (nombre + ROW_ID) sobre índice,
      // para no pisar por accidente el estado local de OTRA prueba si currentPruebaIndex quedó
      // obsoleto.
      const updatePrueba = (pr: any, idx: number) => {
        const matchesByContent = !!existingRowId && pr.ROW_ID === String(existingRowId) && pr.nombre === currentPrueba;
        const matchesByIndex = !existingRowId && idx === currentPruebaIndex;
        return (matchesByContent || matchesByIndex)
          ? { ...pr, ROW_ID: responseRowId, nomId: savedNomId, ACEPTADO: responseAceptado, EN_BORRADOR: false, MOSTRAR_OBSERVACIONES: formData?.mostrarObservaciones === true, ...editadoFields }
          : pr;
      };

      setPedidosState(prev => prev.map(p =>
        p.id === pedidoId ? { ...p, pruebasSolicitadas: p.pruebasSolicitadas.map(updatePrueba) } : p
      ));
      setSelectedPedido(prev =>
        prev && prev.id === pedidoId
          ? { ...prev, pruebasSolicitadas: prev.pruebasSolicitadas.map(updatePrueba) }
          : prev
      );

      toast.success(isEditMode ? "Prueba editada y aceptada" : "Prueba guardada exitosamente");
      setCurrentView('detail');
      setCurrentPrueba('');
    } catch (err: any) {
      toast.error("Error al guardar: " + (err.message || err));
      setCurrentView('detail');
      setCurrentPrueba('');
    } finally {
      isSavingRef.current = false;
    }
  };

  const handleRevisarPrueba = async (pedido: Pedido, prueba: string, rowId?: string) => {
    if (!rowId) {
      alert('No hay datos guardados para esta prueba.');
      return;
    }
    const key = `${prueba}__${rowId}`;
    setSelectedPruebaKey(key);
    // Set index so handleSaveForm can find the correct existingNomId when editing
    const pruebaIdx = pedido.pruebasSolicitadas.findIndex(pr => pr.nombre === prueba && pr.ROW_ID === rowId);
    setCurrentPruebaIndex(pruebaIdx);
    try {
      const row = await revisarPrueba(prueba, rowId);
      const mappedData = supabaseToFormData(prueba, row);
      // ACEPTADO comes from noms (loaded via pruebasSolicitadas), not the prueba table
      const pruebaObj = pedido.pruebasSolicitadas.find(pr => pr.nombre === prueba && pr.ROW_ID === rowId);
      initialDataCacheRef.current = null;
      setFormInitialData({ ...mappedData, ROW_ID: rowId, ACEPTADO: pruebaObj ? (pruebaObj.ACEPTADO || '') : (row.aceptado || ''), MOSTRAR_OBSERVACIONES: pruebaObj?.MOSTRAR_OBSERVACIONES === true });
      setFormMode('view');
      setSelectedPedido(pedido);
      setCurrentPrueba(prueba);
      setCurrentView('form');
    } catch (err: any) {
      alert("Error al consultar: " + (err.message || err));
    }
  };

  const handleEditarPrueba = async (pedido: Pedido, prueba: string, rowId?: string) => {
    if (!rowId) {
      alert('No hay datos guardados para esta prueba.');
      return;
    }
    const key = `${prueba}__${rowId}`;
    setSelectedPruebaKey(key);
    const pruebaIdx = pedido.pruebasSolicitadas.findIndex(pr => pr.nombre === prueba && pr.ROW_ID === rowId);
    setCurrentPruebaIndex(pruebaIdx);
    try {
      const row = await revisarPrueba(prueba, rowId);
      const mappedData = supabaseToFormData(prueba, row);
      const pruebaObj = pedido.pruebasSolicitadas.find(pr => pr.nombre === prueba && pr.ROW_ID === rowId);
      initialDataCacheRef.current = null;
      setFormInitialData({ ...mappedData, ROW_ID: rowId, nomId: pruebaObj?.nomId || '', ACEPTADO: pruebaObj ? (pruebaObj.ACEPTADO || '') : (row.aceptado || ''), MOSTRAR_OBSERVACIONES: pruebaObj?.MOSTRAR_OBSERVACIONES === true, _EDIT_MODE: true });
      setFormMode('admin-edit');
      setSelectedPedido(pedido);
      setCurrentPrueba(prueba);
      setCurrentView('form');
    } catch (err: any) {
      alert("Error al consultar: " + (err.message || err));
    }
  };

  // ─── FORM VIEW ─────────────────────────────────────────────────────────────

  if (currentView === 'form' && selectedPedido && currentPrueba) {
    const isReviewForm = formMode === 'view';
    const initialDataForm = (formMode === 'view' || formMode === 'admin-edit' || formMode === 'continue-draft') ? formInitialData : undefined;
    const modeForm = isReviewForm ? 'view' : 'create';

    // Helper to resolve rowId and aceptado from the composite key
    const resolveRowAndAceptado = () => {
      let rowId = '';
      let aceptado = '';
      let pruebaObj: any = null;
      const selectedKey = selectedPruebaKey || '';

      if (latestSelectedPedido?.pruebasSolicitadas && selectedKey) {
        const [nombre, row] = selectedKey.split('__');
        pruebaObj = latestSelectedPedido.pruebasSolicitadas.find(
          (pr: any) => pr.nombre === nombre && String(pr.ROW_ID) === String(row)
        );
        if (pruebaObj) {
          rowId = pruebaObj.ROW_ID || '';
          aceptado = pruebaObj.ACEPTADO || '';
        }
      }
      if (!rowId && latestSelectedPedido?.pruebasSolicitadas) {
        // Use index if available to avoid matching the wrong prueba when duplicates exist
        pruebaObj = pruebaObj || (
          currentPruebaIndex >= 0
            ? latestSelectedPedido.pruebasSolicitadas[currentPruebaIndex]
            : latestSelectedPedido.pruebasSolicitadas.find((pr: any) => pr.nombre === currentPrueba)
        );
        if (pruebaObj) {
          rowId = pruebaObj.ROW_ID || '';
          aceptado = pruebaObj.ACEPTADO || '';
        }
      }
      if (!rowId && initialDataForm) rowId = initialDataForm.ROW_ID || initialDataForm.row_id || '';
      // Solo usar initialDataForm como fuente de aceptado si no se encontró pruebaObj (noms);
      // si pruebaObj existe, su valor manda aunque sea '' (no aceptado), para no pisar el
      // estado real de noms con un snapshot potencialmente obsoleto.
      if (!pruebaObj && initialDataForm) aceptado = initialDataForm.ACEPTADO || initialDataForm.aceptado || '';
      return { rowId, aceptado, pruebaObj };
    };

    // Computa initialData una sola vez por sesión de formulario y cachea el resultado
    // (ver initialDataCacheRef arriba). Sin esto, cada re-render de fondo (ej. el autosave
    // optimista) reconstruye initialData con un snapshot viejo y pisa lo que el usuario
    // ya escribió — es la causa del bug donde un campo "se borra" sin que el usuario lo borre.
    const getStableInitialData = (computeFresh: () => any) => {
      if (initialDataCacheRef.current) return initialDataCacheRef.current.value;
      const value = computeFresh();
      initialDataCacheRef.current = { value };
      return value;
    };

    const pruebaNombre = (() => {
      const selectedKey = selectedPruebaKey || '';
      if (latestSelectedPedido?.pruebasSolicitadas && selectedKey) {
        const [nombre, row] = selectedKey.split('__');
        const found = latestSelectedPedido.pruebasSolicitadas.find(pr => pr.nombre === nombre && pr.ROW_ID === row);
        if (found) return found.nombre;
      }
      return currentPrueba;
    })();

    // --- NMX-A-083 ---
    if (currentPrueba.includes('NMX-A-083-INNTEX-2020')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA083Form
          onBack={handleBackToDetail}
          pedidoId={(latestSelectedPedido as Pedido).numeroPedido}
          pedidoRowId={(latestSelectedPedido as Pedido).id}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          realizoUser={userName}
          onSave={handleSaveForm}
          onRefreshBackend={onRefreshBackend}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-109 ---
    if (pruebaNombre.includes('NMX-A-109-INNTEX-2012 RESISTENCIA AL RASGADO')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA109Form
          pedidoNumero={selectedPedido.numeroPedido}
          pedidoRowId={selectedPedido.id}
          onBack={handleBackToDetail}
          onSave={handleSaveForm}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          realizoUser={userName}
          onRefreshBackend={onRefreshBackend}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-3801 ---
    if (currentPrueba.includes('NMX-A-3801-INNTEX-2012')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA3801Form
          pedidoNumero={selectedPedido.numeroPedido}
          pedidoRowId={selectedPedido.id}
          onBack={handleBackToDetail}
          onSave={handleSaveForm}
          autorizo={userName}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          onRefreshBackend={onRefreshBackend}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-134 ---
    if (currentPrueba.includes('NMX-A-134-INNTEX-2013 DENSIDAD DE TEJIDO DE PUNTO')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA134Form
          pedidoNumero={selectedPedido.numeroPedido}
          pedidoRowId={selectedPedido.id}
          onBack={handleBackToDetail}
          onSave={handleSaveForm}
          realizoUser={userName}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          onRefreshBackend={onRefreshBackend}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- TIPO DE TEJIDO MET-7.2-11 ---
    if (currentPrueba.includes('TIPO DE TEJIDO MET-7.2-11')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <MET7211Form
          pedidoNumero={selectedPedido.numeroPedido}
          pedidoRowId={selectedPedido.id}
          onBack={handleBackToDetail}
          onSave={handleSaveForm}
          realizoUser={userName}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          onRefreshBackend={onRefreshBackend}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- TIPO DE LIGAMENTO MET-7.2-12 ---
    if (currentPrueba.includes('TIPO DE LIGAMENTO MET-7.2-12')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      const idPedido = latestSelectedPedido?.id || latestSelectedPedido?.numeroPedido || '';
      let mappedData = undefined;
      if (isReviewForm && initialDataForm) {
        const get = (...keys: string[]) => {
          for (const k of keys) {
            if (initialDataForm[k] !== undefined && initialDataForm[k] !== null) return String(initialDataForm[k]);
          }
          return "";
        };
        mappedData = {
          fecha: get("FECHA", "fecha"),
          imagenUrl: get("IMAGEN_URL", "imagenUrl"),
          resultadoPlano: get("RES_PLANO_LIG", "resultadoPlano"),
          resultadoPunto: get("RES_PUNTO_LIG", "resultadoPunto"),
          observaciones: get("OBSERVACIONES", "observaciones"),
          realizo: get("REALIZO", "realizo"),
          superviso: get("SUPERVISO", "superviso"),
          numeroSolicitud: get("SOLICITUD", "numeroSolicitud"),
          tipoTejido: get("TIPO_TEJIDO", "tipoTejido"),
          PRUEBA: "TIPO DE LIGAMENTO MET-7.2-12",
          PEDIDO: idPedido,
          ROW_ID: rowId,
          nomId: pruebaObj?.nomId || '',
          ACEPTADO: aceptado,
        };
      }
      return (
        <MET7212Form
          pedidoNumero={selectedPedido.numeroPedido}
          pedidoRowId={selectedPedido.id}
          onBack={handleBackToDetail}
          onSave={handleSaveForm}
          realizoUser={userName}
          initialData={getStableInitialData(() => isReviewForm && mappedData ? mappedData : { ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado, PEDIDO: selectedPedido.id })}
          mode={isReviewForm ? 'view' : 'create'}
          onRefreshBackend={onRefreshBackend}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-275/5 ---
    if (currentPrueba.includes('NMX-A-275/5-INNTEX-2000 DENSIDAD LINEAL DE HILOS EXTRAIDOS DE LA TELA')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA2755Form
          pedidoNumero={selectedPedido.numeroPedido}
          pedidoRowId={selectedPedido.id}
          onBack={handleBackToDetail}
          onSave={handleSaveForm}
          realizoUser={userName}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          onRefreshBackend={onRefreshBackend}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-172 ---
    if (pruebaNombre.includes('NMX-A-172-INNTEX-2012 RESISTENCIA A LA ABRASIÓN PLANA TABER')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA172Form
          pedidoId={selectedPedido.numeroPedido}
          pedidoRowId={selectedPedido.id}
          onBack={handleBackToDetail}
          realizoUser={userName}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          onSave={handleSaveForm}
          onRefreshBackend={onRefreshBackend}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-9073/1 ---
    if (currentPrueba.includes('NMX-A-9073/1-INNTEX-2018 MASA DE NO TEJIDOS')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA90731Form
          onBack={handleBackToDetail}
          pedidoId={selectedPedido.numeroPedido}
          pedidoRowId={selectedPedido.id}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          realizoUser={userName}
          onSave={handleSaveForm}
          onRefreshBackend={onRefreshBackend}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-4920 ---
    if (currentPrueba.includes('NMX-A-4920-INNTEX-2017 RESISTENCIA AL HUMEDECIMIENTO SUPERFICIAL')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA4920Form
          pedidoId={selectedPedido.numeroPedido}
          pedidoRowId={selectedPedido.id}
          onBack={handleBackToDetail}
          realizoUser={userName}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          onSave={handleSaveForm}
          onRefreshBackend={onRefreshBackend}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-12945-3 ---
    if (currentPrueba.includes('NMX-A-12945-3-INNTEX-2020 DETERMINACIÓN DE FRISAS')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA129453Form
          pedidoId={selectedPedido.numeroPedido}
          pedidoRowId={selectedPedido.id}
          onBack={handleBackToDetail}
          realizoUser={userName}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          onSave={handleSaveForm}
          onRefreshBackend={onRefreshBackend}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-083 (duplicate catch for 'EVALUACIÓN DE CONFECCIÓN') ---
    if (currentPrueba && currentPrueba.includes('NMX-A-083-INNTEX-2020 EVALUACIÓN DE CONFECCIÓN Y DIMENSIÓN DE PRENDAS')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA083Form
          onBack={handleBackToDetail}
          pedidoId={(latestSelectedPedido as Pedido).numeroPedido}
          pedidoRowId={(latestSelectedPedido as Pedido).id}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          realizoUser={userName}
          onSave={handleSaveForm}
          onRefreshBackend={onRefreshBackend}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-229 ---
    if (pruebaNombre.includes('NMX-A-229-1982 Determinación del pH y Δ pH de un extracto acuoso de cuero')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA229Form
          onBack={handleBackToDetail}
          pedidoNumero={selectedPedido.numeroPedido}
          pedidoRowId={selectedPedido.id}
          onSave={handleSaveForm}
          autorizo={userName}
          mode={modeForm}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          onRefreshBackend={onRefreshBackend}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-221 ---
    if (currentPrueba.includes('NMX-A-221-1982 Determinación de las grasas y otros materiales solubles por extracción con cloruro de metileno')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA221Form
          onBack={handleBackToDetail}
          pedidoId={selectedPedido.numeroPedido}
          pedidoRowId={selectedPedido.id}
          mode={modeForm}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          realizoUser={userName}
          onSave={handleSaveForm}
          rol={userRol}
          onRefreshBackend={onRefreshBackend}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-225 ---
    if (currentPrueba.includes('NMX-A-225-1982 Determinación de humedad en cuero')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA225Form
          pedidoId={selectedPedido.numeroPedido}
          pedidoRowId={selectedPedido.id}
          onBack={handleBackToDetail}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          realizoUser={userName}
          onSave={handleSaveForm}
          onRefreshBackend={onRefreshBackend}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-228 ---
    if (currentPrueba.includes('NMX-A-228-1982 Determinación de cenizas totales y cenizas insolubles en agua')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA228Form
          onBack={handleBackToDetail}
          pedidoId={selectedPedido.numeroPedido}
          pedidoRowId={selectedPedido.id}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          realizoUser={userName}
          onSave={handleSaveForm}
          onRefreshBackend={onRefreshBackend}
          rol={userRol}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-230 ---
    if (pruebaNombre.includes('NMX-A-230-1982 Determinación del contenido de cromo')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA230Form
          onBack={handleBackToDetail}
          pedidoId={selectedPedido.numeroPedido}
          pedidoRowId={selectedPedido.id}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          realizoUser={userName}
          onSave={handleSaveForm}
          onRefreshBackend={onRefreshBackend}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- ANÁLISIS QUÍMICO ---
    if (currentPrueba.includes('ANÁLISIS QUÍMICO CUALITATIVO Y CUANTITATIVO')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <AnalisisQuimicoForm
          onBack={handleBackToDetail}
          pedidoId={selectedPedido.numeroPedido}
          pedidoRowId={selectedPedido.id}
          mode={modeForm}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          realizoUser={userName}
          onSave={handleSaveForm}
          onRefreshBackend={onRefreshBackend}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-073 ---
    if (currentPrueba.includes('NMX-A-073-INNTEX-2005 SOLIDEZ DEL COLOR AL FROTE')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXa073Form
          onCancel={handleBackToDetail}
          onSave={handleSaveForm}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          realizoUser={userName}
          onRefreshBackend={onRefreshBackend}
          pedidoRowId={selectedPedido.id}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-105-C06 ---
    if (currentPrueba.includes('NMX-A-105-C06-INNTEX-2015 SOLIDEZ DEL COLOR AL LAVADO DOMESTICO E INDUSTRIAL')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA105C06Form
          onCancel={handleBackToDetail}
          onSave={handleSaveForm}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          realizoUser={userName}
          onRefreshBackend={onRefreshBackend}
          pedidoRowId={selectedPedido.id}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-105-E04 ---
    if (currentPrueba.includes('NMX-A-105-E04-INNTEX-2019 SOLIDEZ DEL COLOR AL SUDOR')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA105E04Form
          onCancel={handleBackToDetail}
          onSave={handleSaveForm}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          realizoUser={userName}
          onRefreshBackend={onRefreshBackend}
          pedidoRowId={selectedPedido.id}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- TIPO DE ACABADO MET-7.2-19 ---
    if (currentPrueba.includes('TIPO DE ACABADO MET-7.2-19')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      let mappedInitialData = initialDataForm || {};
      if (initialDataForm && modeForm === 'view') {
        mappedInitialData = {
          fecha: initialDataForm.fecha || initialDataForm.FECHA || '',
          resultado: initialDataForm.resultado || initialDataForm.RESULTADO || '',
          realizo: initialDataForm.realizo || initialDataForm.REALIZO || '',
          superviso: initialDataForm.superviso || initialDataForm.SUPERVISO || '',
          observaciones: initialDataForm.observaciones || initialDataForm.OBSERVACIONES || '',
          PRUEBA: initialDataForm.PRUEBA || '',
          PEDIDO: initialDataForm.PEDIDO || '',
          numero_solicitud: initialDataForm.numero_solicitud || initialDataForm.SOLICITUD || '',
          ROW_ID: rowId,
          ACEPTADO: aceptado,
        };
      }
      return (
        <MET7219Form
          onCancel={handleBackToDetail}
          onSave={handleSaveForm}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...mappedInitialData, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          realizoUser={userName}
          onRefreshBackend={onRefreshBackend}
          pedidoRowId={selectedPedido.id}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-5077/6330 ---
    if (pruebaNombre.includes('NMX-A-5077-INNTEX-2015 / NMX-A-6330-INNTEX-2015 CAMBIOS DIMENSIONALES')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA50776330Form
          onCancel={handleBackToDetail}
          onSave={handleSaveForm}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          rol={userRol}
          realizoUser={userName}
          onRefreshBackend={onRefreshBackend}
          pedidoRowId={selectedPedido.id}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-7211/2 ---
    if (currentPrueba.includes('NMX-A-7211/2-INNTEX-2015 DENSIDAD DE TEJIDO PLANO (METODO A)')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA72112Form
          mode={modeForm}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          onSave={handleSaveForm}
          onCancel={handleBackToDetail}
          pedidoId={selectedPedido.numeroPedido}
          pedidoRowId={selectedPedido.id}
          realizoUser={userName}
          onRefreshBackend={onRefreshBackend}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-105-D01 ---
    if (currentPrueba.includes('NMX-A-105-D01-INNTEX-2020')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA105D01Form
          onCancel={handleBackToDetail}
          onSave={handleSaveForm}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          realizoUser={userName}
          onRefreshBackend={onRefreshBackend}
          pedidoRowId={selectedPedido.id}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-105-B02 ---
    if (currentPrueba.includes('NMX-A-105-B02-INNTEX-2019')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA105B02Form
          onCancel={handleBackToDetail}
          onSave={handleSaveForm}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          realizoUser={userName}
          onRefreshBackend={onRefreshBackend}
          pedidoRowId={selectedPedido.id}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-059/1 ---
    if (currentPrueba.includes('NMX-A-059/1-INNTEX-2019')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA059_1Form
          onCancel={handleBackToDetail}
          onSave={handleSaveForm}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          realizoUser={userName}
          onRefreshBackend={onRefreshBackend}
          pedidoRowId={selectedPedido.id}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-A-059/2 ---
    if (currentPrueba.includes('NMX-A-059/2-INNTEX-2019') || currentPrueba.includes('059/2') || currentPrueba.includes('Grab')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXA059_2Form
          onCancel={handleBackToDetail}
          onSave={handleSaveForm}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          realizoUser={userName}
          onRefreshBackend={onRefreshBackend}
          pedidoRowId={selectedPedido.id}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- AATCC TM127 ---
    if (currentPrueba.includes('AATCC TM127-2017')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <AATCCForm
          onCancel={handleBackToDetail}
          onSave={handleSaveForm}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          realizoUser={userName}
          onRefreshBackend={onRefreshBackend}
          pedidoRowId={selectedPedido.id}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- NMX-S-051 ---
    if (currentPrueba.includes('NMX-S-051-1989')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <NMXS051Form
          onCancel={handleBackToDetail}
          onSave={handleSaveForm}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          realizoUser={userName}
          onRefreshBackend={onRefreshBackend}
          pedidoRowId={selectedPedido.id}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- PRUEBA ADICIONAL ---
    if (currentPrueba.includes('PRUEBA ADICIONAL')) {
      const { rowId, aceptado, pruebaObj } = resolveRowAndAceptado();
      return (
        <PruebaAdicionalForm
          onCancel={handleBackToDetail}
          onSave={handleSaveForm}
          initialData={getStableInitialData(() => ({ ...pruebaObj, ...initialDataForm, ROW_ID: rowId, ACEPTADO: aceptado }))}
          mode={modeForm}
          realizoUser={userName}
          onRefreshBackend={onRefreshBackend}
          pedidoRowId={selectedPedido.id}
          onAutosave={handleAutosavePrueba}
        />
      );
    }

    // --- Fallback ---
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleBackToDetail}>
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Button>
          <h1>Formulario de Prueba</h1>
        </div>
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground mb-4">
              Formulario para "{currentPrueba}" en desarrollo
            </p>
            <Button onClick={handleBackToDetail}>
              Volver al Detalle
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── DETAIL VIEW ───────────────────────────────────────────────────────────

  if (currentView === 'detail' && selectedPedido) {
    const pedidoActual = pedidosState.find(p => p.id === selectedPedido.id) || selectedPedido;
    return (
      <>
        <PedidoDetail
          pedido={pedidoActual}
          onBack={handleBackToList}
          onAddPrueba={handleAddPrueba}
          onLlenarPrueba={handleLlenarPrueba}
          pruebasGuardadas={pruebasGuardadas}
          handleRevisarPrueba={handleRevisarPrueba}
          handleEditarPrueba={handleEditarPrueba}
          handleContinuarBorrador={handleContinuarBorrador}
          onToggleReporteCliente={handleToggleReporteCliente}
          onExpirarModoAdmin={async () => {
            await supabase
              .from('pedido_acceso_edicion')
              .delete()
              .eq('pedido_id', selectedPedido.id)
              .eq('email_autorizado', userEmail);
            setModoEdicionAdmin(false);
          }}
          setPedidosState={setPedidosState}
          onRefreshBackend={onRefreshBackend}
          showDocumentoUploadModal={showDocumentoUploadModal}
          setShowDocumentoUploadModal={setShowDocumentoUploadModal}
          documentoUploadInfo={documentoUploadInfo}
          setDocumentoUploadInfo={setDocumentoUploadInfo}
          modoEdicionAdmin={modoEdicionAdmin}
          puedeCapturar={puedeCapturar}
          puedeCerrarPedido={puedeCerrarPedido}
          puedeVerBitacoras={puedeVerBitacoras}
          puedeReabrir={puedeReabrir}
          userName={userName}
        />

        {/* Modal secreto — solo visible para admins vía ?admin_edit=true */}
        {showAdminModal && esAdmin && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-sm shadow-xl flex flex-col gap-4">
              <h3 className="font-semibold text-gray-800">Habilitar edición</h3>
              <div className="text-xs text-gray-500 bg-gray-50 rounded px-3 py-2">
                Pedido: <span className="font-medium text-gray-700">{pedidoActual.numeroPedido}</span>
              </div>
              <div className="space-y-1">
                <label className="text-sm text-gray-600">Correo a autorizar</label>
                <input
                  type="email"
                  className="w-full border rounded px-3 py-2 text-sm"
                  placeholder="correo@ejemplo.com"
                  value={adminModalEmail}
                  onChange={e => setAdminModalEmail(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm text-gray-600">Horas de acceso</label>
                <input
                  type="number"
                  className="w-full border rounded px-3 py-2 text-sm"
                  min="1"
                  max="720"
                  value={adminModalHoras}
                  onChange={e => setAdminModalHoras(e.target.value)}
                />
              </div>
              <div className="flex gap-2 justify-end pt-1">
                <button
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setShowAdminModal(false)}
                >
                  Cancelar
                </button>
                <button
                  className="px-4 py-2 text-sm text-white rounded disabled:opacity-50"
                  style={{ backgroundColor: '#1d4ed8' }}
                  disabled={adminModalGuardando || !adminModalEmail}
                  onClick={handleHabilitarAcceso}
                >
                  {adminModalGuardando ? 'Guardando...' : 'Habilitar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // ─── LIST VIEW ─────────────────────────────────────────────────────────────

  return (
    <>
      {puedeAutorizarReapertura && (
        <ReaperturasPendientesPanel userName={userName} onRefreshBackend={onRefreshBackend} />
      )}
      <PedidosList
        pedidos={pedidosState}
        searchTerm={filtroTarjeta}
        onSearchChange={setFiltroTarjeta}
        onSelect={(pedido) => {
          setSelectedPedido(pedido);
          setCurrentView('detail');
        }}
      />
    </>
  );
}
